import styled from '@emotion/styled';
import Flex from '@react-css/flex';
import { forwardRef } from 'react';
import type { FieldError } from 'react-hook-form';

import type { FieldAction } from 'components/Common/FormField';

import Tooltip from 'storybookConfig/stories/cells/Tooltip';
import Body from 'storybookConfig/stories/molecules/Body';
import IconButton from 'storybookConfig/stories/molecules/Button/IconButton';
import Icon from 'storybookConfig/stories/molecules/Icon';
import Input from 'storybookConfig/stories/molecules/Input';
import Label from 'storybookConfig/stories/molecules/Label';

export interface CardFormFieldProps extends React.HTMLProps<HTMLInputElement> {
  error?: { message: string } | FieldError;
  label: string;
  id: string;
  type: string;
  className?: string;
  tooltipText?: string;
  tooltipPlacement?: string;
  appendInput?: FieldAction[];
}

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

/**
 * A labelled field for a card's form, drawn with the DS: the label (and its tooltip) above a 44px
 * `Input`, any `appendInput` actions as icon buttons beside it, and the error below.
 * @deprecated Use `FormField` from `storybookConfig/stories/cells/FormField` with an `Input`.
 */
const CardFormField = forwardRef<HTMLInputElement, CardFormFieldProps>((props, ref) => {
  const {
    error,
    label,
    id,
    type,
    className,
    tooltipText,
    tooltipPlacement,
    appendInput,
    size,
    as,
    ...inputProps
  } = props;
  const errorId = error ? `${id}-error` : undefined;

  return (
    <Field className={className}>
      <Label htmlFor={id} hasError={!!error}>
        {label}
        {tooltipText && (
          <Tooltip>
            <Tooltip.Trigger asChild>
              <Icon name="circle-question-mark" position="after" />
            </Tooltip.Trigger>
            <Tooltip.Content>{tooltipText}</Tooltip.Content>
          </Tooltip>
        )}
      </Label>

      <Flex alignItemsCenter gap="8px">
        <Input
          type={type}
          id={id}
          ref={ref}
          isFullWidth
          aria-invalid={!!error}
          aria-describedby={errorId}
          {...inputProps}
        />
        {appendInput?.map((input, index) => (
          <IconButton
            // eslint-disable-next-line react/no-array-index-key
            key={`${input.name}-${index}`}
            type="button"
            name={input.icon}
            aria-label={input.label ? `${input.label} ${label}` : input.name}
            onClick={() => input.event(input.eventData)}
          />
        ))}
      </Flex>

      {error && (
        <Body id={errorId} variant="Body/Body Small" color="errorBase">
          {error.message}
        </Body>
      )}
    </Field>
  );
});

export default CardFormField;
